import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Image, FileText, CheckCircle, XCircle, User, Calendar, Download, Phone, RotateCcw, Eye, Filter, Search, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { getWithTokenAPI, postWithTokenAPI, putWithTokenAPI } from "../../util/Api";
import Swal from 'sweetalert2';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
// Approval status enum
const APPROVAL_STATUS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected"
};

// Filter options
const FILTER_OPTIONS = {
  ALL: "all",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected"
};

const ContentApprovalsSection = () => {
  const [approvals, setApprovals] = useState({
    loading: false,
    error: null,
    documents: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalDocuments: 0,
      hasNext: false,
      hasPrev: false
    }
  });
  const [allDocuments, setAllDocuments] = useState([]); // Store all documents for accurate counts
  const [actionLoading, setActionLoading] = useState(null);
  const auth = useSelector((state) => state.auth)
  const admin_id = auth?.user?._id
  const [userDetails, setUserDetails] = useState({});
  const [filter, setFilter] = useState(FILTER_OPTIONS.ALL);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });

  let searchParam = "";

  if (filter === FILTER_OPTIONS.PENDING) searchParam = "&search=pending";
  if (filter === FILTER_OPTIONS.APPROVED) searchParam = "&search=approved";
  if (filter === FILTER_OPTIONS.REJECTED) searchParam = "&search=rejected";



  useEffect(() => {
    getApprovals(1, pagination.limit);
  }, [filter]);


  // Function to fetch user details
  const fetchUserDetails = async (userIds) => {
    const token = localStorage.getItem('matrimony_token');
    if (!token) return;

    const uniqueUserIds = [...new Set(userIds.filter(id => id && id !== "null" && id !== ""))];

    const userDetailsMap = {};

    for (const userId of uniqueUserIds) {
      try {
        // Try different endpoints to get user details
        const endpoints = [
          `/admin/get-user-profile/${userId}`,
          `/profile/get_all/${userId}`,
          `/users/${userId}`,
          `/user/${userId}`
        ];

        let userData = null;

        for (const endpoint of endpoints) {
          try {
            const response = await getWithTokenAPI(token, endpoint);

            if (response && (response.statusCode === 200 || response.success)) {
              // Handle different response structures
              userData = response.data || response.user || response.profile || response;
              if (userData) break;
            }
          } catch (error) {
            console.log(`Endpoint ${endpoint} failed for user ${userId}:`, error.message);
            continue;
          }
        }

        if (userData) {
          userDetailsMap[userId] = {
            name: userData.basicProfile?.name || userData.name || 'Unknown User',
            mobile_number: userData.basicProfile?.mobile_number || userData.mobile_number || 'N/A'
          };
        } else {
          userDetailsMap[userId] = {
            name: 'Unknown User',
            mobile_number: 'N/A'
          };
        }
      } catch (error) {
        console.error(`Error fetching details for user ${userId}:`, error);
        userDetailsMap[userId] = {
          name: 'Error Loading User',
          mobile_number: 'N/A'
        };
      }
    }

    setUserDetails(prev => ({ ...prev, ...userDetailsMap }));
    return userDetailsMap;
  };

  // Function to fetch ALL documents for accurate counts
  const fetchAllDocumentsForCounts = async () => {
    try {
      const token = localStorage.getItem('matrimony_token');
      if (!token) return;

      // Fetch all documents without pagination for counts
      const response = await getWithTokenAPI(token, `/document/s3?page=1&limit=1000`); // Large limit to get all

      let allDocs = [];

      // Handle API response structure
      if (response.data && Array.isArray(response.data)) {
        allDocs = response.data;
      } else if (Array.isArray(response)) {
        allDocs = response;
      } else {
        allDocs = response.data || response.documents || [];
      }

      // Filter out unwanted content
      const filteredAllDocs = allDocs
        .filter(doc => {
          if (!doc.user_id || doc.user_id === '' || doc.user_id === 'null') return false;
          if (!doc.url || doc.url === '' || !doc.url.startsWith('http')) return false;
          if (!doc.documentId && !doc.key) return false;
          return true;
        })
        .map(doc => {
          let status = APPROVAL_STATUS.PENDING;

          if (doc.isApproved === true || doc.isApproved === "Approved") {
            status = APPROVAL_STATUS.APPROVED;
          } else if (doc.isApproved === false || doc.isApproved === "Rejected") {
            status = APPROVAL_STATUS.REJECTED;
          } else if (doc.isApproved === null || doc.isApproved === undefined || doc.isApproved === "Pending") {
            status = APPROVAL_STATUS.PENDING;
          }

          return {
            ...doc,
            id: doc.documentId || doc.key,
            type: determineDocumentType(doc.key),
            content: getContentDescription(doc.key),
            timestamp: doc.lastModified ? new Date(doc.lastModified).getTime() : Date.now(),
            fileName: getFileName(doc.key),
            status: status
          };
        });

      // Sort by timestamp - latest first
      const sortedAllDocs = filteredAllDocs.sort((a, b) => b.timestamp - a.timestamp);
      setAllDocuments(sortedAllDocs);

    } catch (error) {
      console.error('Error fetching all documents for counts:', error);
    }
  };

  const getApprovals = async (page = pagination.page, limit = pagination.limit) => {
    setApprovals(prev => ({ ...prev, loading: true, error: null }));

    try {
      const token = localStorage.getItem("matrimony_token");
      if (!token) throw new Error("Please login again");

      // 🔥 Add dynamic filter param
      let searchParam = "";
      if (filter === FILTER_OPTIONS.PENDING) {
        searchParam = "&search=pending";
      } else if (filter === FILTER_OPTIONS.APPROVED) {
        searchParam = "&search=approved";
      } else if (filter === FILTER_OPTIONS.REJECTED) {
        searchParam = "&search=rejected";
      }
      // ALL → no search param

      // 🔥 Updated API call with filter applied
      const response = await getWithTokenAPI(
        token,
        `/document/s3?page=${page}&limit=${limit}${searchParam}`
      );

      console.log("getApprovals response:", response);

      let documents = [];
      let paginationData = {
        currentPage: page,
        totalPages: 1,
        totalDocuments: 0,
        hasNext: false,
        hasPrev: page > 1,
      };

      // Handle API response
      if (response.data && Array.isArray(response.data)) {
        documents = response.data;
        paginationData = {
          currentPage: response.currentPage || page,
          totalPages: response.totalPages || 1,
          totalDocuments: response.totalCount || documents.length,
          hasNext: (response.currentPage || page) < (response.totalPages || 1),
          hasPrev: (response.currentPage || page) > 1,
        };
      } else if (Array.isArray(response)) {
        documents = response;
      } else {
        documents = response.data || response.documents || [];
        paginationData = {
          currentPage: response.currentPage || response.data?.currentPage || page,
          totalPages: response.totalPages || response.data?.totalPages || 1,
          totalDocuments:
            response.totalCount ||
            response.data?.totalCount ||
            response.totalDocuments ||
            documents.length,
          hasNext: (response.currentPage || page) < (response.totalPages || 1),
          hasPrev: (response.currentPage || page) > 1,
        };
      }

      // Filter invalid docs
      const filteredDocuments = documents
        .filter(doc => {
          if (!doc.user_id || doc.user_id === "" || doc.user_id === "null")
            return false;
          if (!doc.url || !doc.url.startsWith("http")) return false;
          if (!doc.documentId && !doc.key) return false;
          return true;
        })
        .map(doc => {
          let status = APPROVAL_STATUS.PENDING;

          if (doc.isApproved === true || doc.isApproved === "Approved") {
            status = APPROVAL_STATUS.APPROVED;
          } else if (doc.isApproved === false || doc.isApproved === "Rejected") {
            status = APPROVAL_STATUS.REJECTED;
          }

          return {
            ...doc,
            id: doc.documentId || doc.key,
            type: determineDocumentType(doc.key),
            content: getContentDescription(doc.key),
            timestamp: doc.lastModified
              ? new Date(doc.lastModified).getTime()
              : Date.now(),
            fileName: getFileName(doc.key),
            status: status,
          };
        });

      // Sort latest first
      const sortedDocuments = filteredDocuments.sort(
        (a, b) => b.timestamp - a.timestamp
      );

      // Fetch user details
      const userIds = sortedDocuments.map(doc => doc.user_id);
      await fetchUserDetails(userIds);

      // Update state
      setApprovals({
        loading: false,
        error: null,
        documents: sortedDocuments,
        pagination: paginationData,
      });

      setPagination(prev => ({
        ...prev,
        page: paginationData.currentPage,
      }));
    } catch (error) {
      console.error("Error fetching approvals:", error);
      setApprovals(prev => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to load approvals",
      }));
    }
  };


  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > approvals.pagination.totalPages) return;
    getApprovals(newPage, pagination.limit);
  };

  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1 // Reset to first page when changing limit
    }));
    getApprovals(1, newLimit);
  };

  // Generate page numbers for pagination with ellipsis for large numbers
  const generatePageNumbers = () => {
    const totalPages = approvals.pagination.totalPages;
    const currentPage = approvals.pagination.currentPage;
    const pageNumbers = [];

    if (totalPages <= 7) {
      // Show all pages if total pages is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show first 3 pages, current page with neighbors, and last 3 pages
      if (currentPage <= 4) {
        // Near the beginning
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // In the middle
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  // Extract just the file name from the key/path
  const getFileName = (key) => {
    if (!key) return 'document';
    return key.split('/').pop() || key;
  };

  // Determine document type based on file key/name
  const determineDocumentType = (key) => {
    if (!key) return 'document';

    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('photo') || lowerKey.includes('image') ||
      lowerKey.includes('.jpg') || lowerKey.includes('.jpeg') ||
      lowerKey.includes('.png') || lowerKey.includes('.gif') ||
      lowerKey.includes('.webp')) {
      return 'photo';
    }
    return 'document';
  };

  // Get content description based on file key
  const getContentDescription = (key) => {
    if (!key) return 'Document upload';

    const fileName = getFileName(key);
    if (fileName.includes('profile')) return 'Profile photo';
    if (fileName.includes('gallery')) return 'Gallery photo';
    if (fileName.includes('document')) return 'Supporting document';

    return `File: ${fileName}`;
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';

    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;

    return 'Just now';
  };

  // Filter documents based on selected filter and search term
  const getFilteredDocuments = () => {
    return approvals.documents.filter(doc => {
      const matchesFilter = filter === FILTER_OPTIONS.ALL ||
        (filter === FILTER_OPTIONS.PENDING && doc.status === APPROVAL_STATUS.PENDING) ||
        (filter === FILTER_OPTIONS.APPROVED && doc.status === APPROVAL_STATUS.APPROVED) ||
        (filter === FILTER_OPTIONS.REJECTED && doc.status === APPROVAL_STATUS.REJECTED);

      const matchesSearch = searchTerm === "" ||
        doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (userDetails[doc.user_id]?.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (userDetails[doc.user_id]?.mobile_number?.includes(searchTerm));

      return matchesFilter && matchesSearch;
    });
  };

  const handleApprove = async (documentId, user_id) => {
    setActionLoading(documentId);

    try {
      const token = localStorage.getItem('matrimony_token');

      const payload = {
        adminId: admin_id,
        approvalStatus: APPROVAL_STATUS.APPROVED,
        documentIds: [documentId] // Include document ID in payload
      };
      console.log('Approve payload:', payload);

      const response = await putWithTokenAPI(
        token,
        `/document/toggle-approval?user_id=${user_id}`,
        payload
      );

      console.log('Approve response:', response);

      if (response.statusCode === 200) {
        // Update the document status in both approvals and allDocuments states
        setApprovals(prev => ({
          ...prev,
          documents: prev.documents.map(doc =>
            doc.id === documentId
              ? { ...doc, status: APPROVAL_STATUS.APPROVED, isApproved: "Approved" }
              : doc
          )
        }));

        // Also update in allDocuments for accurate counts
        setAllDocuments(prev => prev.map(doc =>
          doc.id === documentId
            ? { ...doc, status: APPROVAL_STATUS.APPROVED, isApproved: "Approved" }
            : doc
        ));

        Swal.fire({
          title: 'Approved!',
          text: 'Document has been approved successfully.',
          icon: 'success',
          confirmButtonText: 'OK'
        });

      } else {
        throw new Error(response.message || 'Failed to approve document');
      }
    } catch (error) {
      console.error('Error approving document:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to approve document',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (documentId, user_id) => {
    setActionLoading(documentId);

    try {
      const token = localStorage.getItem('matrimony_token');
      const payload = {
        adminId: admin_id,
        approvalStatus: APPROVAL_STATUS.REJECTED,
        documentIds: [documentId] // Include document ID in payload
      };
      console.log('Reject payload:', payload);

      const response = await putWithTokenAPI(
        token,
        `/document/toggle-approval?user_id=${user_id}`,
        payload
      );

      console.log('Reject response:', response);

      if (response.statusCode === 200) {
        // Update the document status in both approvals and allDocuments states
        setApprovals(prev => ({
          ...prev,
          documents: prev.documents.map(doc =>
            doc.id === documentId
              ? { ...doc, status: APPROVAL_STATUS.REJECTED, isApproved: "Rejected" }
              : doc
          )
        }));

        // Also update in allDocuments for accurate counts
        setAllDocuments(prev => prev.map(doc =>
          doc.id === documentId
            ? { ...doc, status: APPROVAL_STATUS.REJECTED, isApproved: "Rejected" }
            : doc
        ));

        Swal.fire({
          title: 'Rejected!',
          text: 'Document has been rejected.',
          icon: 'success',
          confirmButtonText: 'OK'
        });

      } else {
        throw new Error(response.message || 'Failed to reject document');
      }
    } catch (error) {
      console.error('Error rejecting document:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to reject document',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Enhanced download handler with better file naming
  const handleDownload = async (file) => {
    try {
      const token = localStorage.getItem("matrimony_token");

      Swal.fire({
        title: "Preparing Download...",
        text: "Please wait while we prepare your file for download.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await fetch(file.url, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a better filename
      const userInfo = userDetails[file.user_id] || {};
      const userName = userInfo.name ? userInfo.name.replace(/\s+/g, '_') : 'user';
      const fileExtension = file.fileName.split('.').pop() || 'jpg';
      const downloadFileName = `${userName}_${file.fileName}`;

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
      Swal.close();

      Swal.fire({
        title: "Download Started!",
        text: `"${downloadFileName}" is downloading to your Downloads folder.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Download failed:", error);
      Swal.close();

      // Fallback - direct download
      const userInfo = userDetails[file.user_id] || {};
      const userName = userInfo.name ? userInfo.name.replace(/\s+/g, '_') : 'user';
      const downloadFileName = `${userName}_${file.fileName}`;

      const link = document.createElement("a");
      link.href = file.url;
      link.download = downloadFileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        title: "Download Initiated!",
        text: "File download has been initiated in a new tab.",
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  // Click handler for the download icon or image
  const handleImageClick = (file, event) => {
    event.stopPropagation();
    handleDownload(file);
  };

  // Enhanced document preview with download option
  const handleViewDocument = (document) => {
    const userInfo = userDetails[document.user_id] || {};

    const previewContent = `
      <div class="document-preview-container" style="max-height: 70vh; display: flex; flex-direction: column; gap: 16px;">
        <div class="document-image-container" style="flex: 1; min-height: 300px; max-height: 400px; display: flex; align-items: center; justify-content: center; background: #f8f9fa; border-radius: 8px; padding: 16px; position: relative; overflow: hidden;">
          <img 
            src="${document.url}" 
            alt="Document preview" 
            style="max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 4px;"
            onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
          />
          <div style="display: none; flex-direction: column; align-items: center; justify-content: center; text-align: center; color: #6b7280; padding: 20px;">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
            <p style="margin-top: 12px; font-size: 14px; font-weight: 500;">Preview not available</p>
            <p style="font-size: 12px; margin-top: 4px;">Click download to get the file</p>
          </div>
         
        </div>
        
          </div>
        </div>
      </div>
    `;

    // Set up global function for download
    window.downloadDocument = (url, fileName, userId) => {
      handleDownload({ url, fileName, user_id: userId });
    };

    Swal.fire({
      title: '<div style="text-align: left; font-size: 18px; font-weight: 600; color: #111827;">Document Preview</div>',
      html: previewContent,
      showCloseButton: true,
      showConfirmButton: false,
      width: '800px',
      padding: '0',
      customClass: {
        popup: 'custom-swal-popup',
        htmlContainer: 'custom-swal-html',
        header: 'custom-swal-header'
      },
      willClose: () => {
        // Clean up the global function
        delete window.downloadDocument;
      }
    });
  };

  useEffect(() => {
    getApprovals(pagination.page, pagination.limit);
    fetchAllDocumentsForCounts(); // Fetch all documents for accurate counts
  }, []);


  useEffect(() => {
  const params = new URLSearchParams(location.search);
  const approval = params.get("approvals");

  if (approval === "pending") {
    setFilter(FILTER_OPTIONS.PENDING);
  } else if (approval === "approved") {
    setFilter(FILTER_OPTIONS.APPROVED);
  } else if (approval === "rejected") {
    setFilter(FILTER_OPTIONS.REJECTED);
  } else {
    setFilter(FILTER_OPTIONS.ALL);
  }
}, [location.search]);

  // Calculate counts for badges - using ALL documents for accurate totals
  const pendingCount = allDocuments.filter(doc => doc.status === APPROVAL_STATUS.PENDING).length;
  const approvedCount = allDocuments.filter(doc => doc.status === APPROVAL_STATUS.APPROVED).length;
  const rejectedCount = allDocuments.filter(doc => doc.status === APPROVAL_STATUS.REJECTED).length;
  const totalCount = allDocuments.length;

  const filteredDocuments = getFilteredDocuments();
  const pageNumbers = generatePageNumbers();
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("documents.title")}</h1>
        </div>
      </div>

      {approvals.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {approvals.error}
        </div>
      )}

      {/* Stats Cards - Single Row Layout */}
      <div className="flex flex-wrap justify-between gap-4 w-full">
        <Card className="flex-1 min-w-[200px] max-w-[300px] border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">  {t("documents.stats.total")}</p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1 min-w-[200px] max-w-[300px] border-l-4 border-l-green-500 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">  {t("documents.stats.approved")}</p>
                <p className="text-2xl font-bold text-gray-900">{approvedCount}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1 min-w-[200px] max-w-[300px] border-l-4 border-l-red-500 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1"> {t("documents.stats.rejected")}</p>
                <p className="text-2xl font-bold text-gray-900">{rejectedCount}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1 min-w-[200px] max-w-[300px] border-l-4 border-l-yellow-500 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1"> {t("documents.stats.pending")}</p>
                <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <Calendar className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Card */}
      <Card >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #e5e7eb",
              padding: "16px",
              margin:"10px",
              borderRadius: "10px",
              // width: "1180px",
              marginBottom: "16px",
            }}
          >
            {/* Left Section */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <CardTitle style={{ fontSize: "20px", fontWeight: "600" }}>
                {t("documents.listTitle")}
              </CardTitle>

              <CardDescription style={{ fontSize: "14px", color: "#6b7280" }}>
                {t("documents.showing", {
                  count: filteredDocuments.length,
                  total: totalCount,
                })}

                {approvals.pagination.totalPages > 1 &&
                  ` - ${t("documents.showingPage", {
                    page: approvals.pagination.currentPage,
                    totalPages: approvals.pagination.totalPages,
                  })}`}
              </CardDescription>
            </div>

            {/* Right Section (Filter + Refresh) */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "12px",
                alignItems: "center",
              }}
            >
              {/* FILTER DROPDOWN */}
              <div style={{ position: "relative" }}>
                <Filter
                  className="text-gray-400"
                  style={{
                    width: "16px",
                    height: "16px",
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />

                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{
                    paddingLeft: "40px",
                    paddingRight: "32px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    width: "160px",
                    fontSize: "14px",
                    cursor: "pointer",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <option value={FILTER_OPTIONS.ALL}>{t("documents.filter.all")}</option>
                  <option value={FILTER_OPTIONS.PENDING}>{t("documents.filter.pending")}</option>
                  <option value={FILTER_OPTIONS.APPROVED}>{t("documents.filter.approved")}</option>
                  <option value={FILTER_OPTIONS.REJECTED}>{t("documents.filter.rejected")}</option>
                </select>
              </div>

              {/* REFRESH BUTTON */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  getApprovals(pagination.page, pagination.limit);
                  fetchAllDocumentsForCounts();
                }}
                disabled={approvals.loading}
                style={{
                  height: "36px",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <RefreshCw
                  className={approvals.loading ? "animate-spin" : ""}
                  style={{ width: "16px", height: "16px" }}
                />
              </Button>
            </div>
          </div>

        <CardContent>
          {approvals.loading ? (
           <div className="space-y-4">
  {[...Array(4)].map((_, i) => (
    <div
      key={i}
      className="flex items-center justify-between p-4 border border-gray-100 rounded-lg bg-white shadow-sm"
    >
      {/* Left */}
      <div className="flex items-center gap-6 flex-1 min-w-0">
        <div className="skeleton w-16 h-16 rounded-lg flex-shrink-0"></div>

        <div className="flex flex-col gap-2 flex-1">
          <div className="skeleton h-4 w-1/4"></div>
          <div className="skeleton h-3 w-1/3"></div>
          <div className="skeleton h-4 w-16"></div>
        </div>
      </div>

      
    </div>
  ))}
</div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">{t("documents.nodataTitle")}</p>
              <p className="text-gray-500 text-sm">
                {searchTerm || filter !== FILTER_OPTIONS.ALL
                  ? t("documents.nodataSearch")
                  : t("documents.nodataDefault")}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {filteredDocuments.map((document) => {
                  const userInfo = userDetails[document.user_id] || {};
                  const documentStatus = document.status;
                  const isProcessing = actionLoading === document.id;
                  const isApproved = documentStatus === APPROVAL_STATUS.APPROVED;
                  const isRejected = documentStatus === APPROVAL_STATUS.REJECTED;
                  const isPending = documentStatus === APPROVAL_STATUS.PENDING;

                  return (
                    <div
                      key={document.id}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-all duration-200 hover:shadow-sm ${isApproved
                        ? 'bg-green-50 border-green-200'
                        : isRejected
                          ? 'bg-red-50 border-red-200'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      {/* Left side - Document info in single row */}
                      <div className="flex items-center gap-6 flex-1">
                        {/* Document Image/Icon with Download */}
                        <div className="flex-shrink-0">
                          {document.type === 'photo' ? (
                            <div className="relative group">
                              <img
                                src={document.url}
                                alt="Document preview"
                                className="w-16 h-16 object-cover rounded-lg border cursor-pointer"
                                onClick={(e) => handleImageClick(document, e)}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                              <div
                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all cursor-pointer flex items-center justify-center opacity-0 group-hover:opacity-100"
                                onClick={(e) => handleImageClick(document, e)}
                              >
                                <Download className="w-5 h-5 text-white" />
                              </div>
                              {/* Download badge */}
                              <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full p-1 cursor-pointer" onClick={(e) => handleImageClick(document, e)}>
                                <Download className="w-3 h-3" />
                              </div>
                            </div>
                          ) : (
                            <div className="relative group">
                              <FileText className="w-16 h-16 text-blue-500 p-3 bg-blue-50 rounded-lg border" />
                              <div
                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all cursor-pointer flex items-center justify-center opacity-0 group-hover:opacity-100"
                                onClick={(e) => handleImageClick(document, e)}
                              >
                                <Download className="w-5 h-5 text-white" />
                              </div>
                              {/* Download badge */}
                              <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full p-1 cursor-pointer" onClick={(e) => handleImageClick(document, e)}>
                                <Download className="w-3 h-3" />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* User Information - Stacked vertically */}
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-900">  {userInfo.name || t("documents.userLoading")}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Phone className="w-3 h-3" />
                            <span>{userInfo.mobile_number || 'Loading...'}</span>
                            <span>•</span>
                            <Calendar className="w-3 h-3" />
                            <span>{formatTimestamp(document.lastModified || document.timestamp)}</span>
                          </div>
                          <div className="mt-1">
                            <Badge variant={
                              isApproved ? "default" :
                                isRejected ? "destructive" :
                                  "secondary"
                            } className={
                              isApproved ? "bg-green-100 text-green-800 hover:bg-green-100 text-xs" :
                                isRejected ? "bg-red-100 text-red-800 hover:bg-red-100 text-xs" :
                                  "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 text-xs"
                            }>
                              {documentStatus}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Right side - Action Buttons */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDocument(document)}
                          title={t("documents.actions.view")}
                          disabled={isProcessing}
                          className="h-9 px-3 cursor-pointer"
                        >
                          <Eye className="w-4 h-4 cursor-pointer" />
                        </Button>

                        {/* Approve Button */}
                        <Button
                          size="sm"
                          className={`h-9 px-4 ${isApproved
                            ? 'bg-green-600 text-white cursor-pointer'
                            : 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                            }`}
                          onClick={() => handleApprove(document.id, document.user_id)}
                          disabled={isProcessing || isApproved}
                        >
                          {isApproved ? t("documents.actions.approved") : t("documents.actions.approve")}

                        </Button>

                        {/* Reject Button */}
                        <Button
                          size="sm"
                          variant={isRejected ? "destructive" : "outline"}
                          onClick={() => handleReject(document.id, document.user_id)}
                          disabled={isProcessing || isRejected}
                          className="h-9 px-4 cursor-pointer"
                        >
                          {isRejected ? t("documents.actions.rejected") : t("documents.actions.reject")}

                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Enhanced Pagination Controls */}
              {approvals.pagination.totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-gray-200 gap-4">
                  <div className="text-sm text-gray-700">
                    {t("documents.pagination.showingPageInfo", {
                      page: approvals.pagination.currentPage,
                      totalPages: approvals.pagination.totalPages,
                      total: totalCount
                    })}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(approvals.pagination.currentPage - 1)}
                      disabled={!approvals.pagination.hasPrev || approvals.loading}
                      className="h-9 px-6 cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4 cursor-pointer" />
                      {t("documents.pagination.previous")}
                    </Button>

                    <div className="flex items-center gap-1 flex-wrap justify-center">
                      {pageNumbers.map((pageNumber, index) => (
                        pageNumber === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        ) : (
                          <Button
                            key={pageNumber}
                            variant={approvals.pagination.currentPage === pageNumber ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNumber)}
                            disabled={approvals.loading}
                            className="h-9 w-10 p-0 min-w-9 cursor-pointer"
                          >
                            {pageNumber}
                          </Button>
                        )
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(approvals.pagination.currentPage + 1)}
                      disabled={!approvals.pagination.hasNext || approvals.loading}
                      className="h-9 px-3 cursor-pointer"
                    >
                      {t("documents.pagination.next")}
                      <ChevronRight className="w-4 h-4 cursor-pointer" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentApprovalsSection;